import { auth } from '@/state/auth'
import axios from 'axios'
import { produce } from 'immer'
import { create } from 'zustand'
import type { ToneData } from '../../functions/_utils/types'
import type { UpdateToneParams } from '../../functions/api/tones/[id]'

type ToneState = {
  loading: boolean
  list: ToneData[]
  reset(): void
  fetch(): Promise<void>
  create(name: string, content: string, defaultToneId?: number): Promise<ToneData>
  update(id: number, name: string, content: string): Promise<ToneData>
  delete(id: number): Promise<void>
}

export const useToneState = create<ToneState>((set, get) => ({
  list: [],
  loading: false,

  fetch: async () => {
    try {
      set({ loading: true })

      const { data } = await auth(token => (
        axios.get<ToneData[]>(`/api/tones`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
      ))

      set({ list: data })
    }
    catch (e) {
      console.error(e)
    }
    finally {
      set({ loading: true })
    }
  },

  create: async (name, content, defaultToneId) => {
    try {
      set({ loading: true })
      const { data } = await auth(token => (
        axios.post<ToneData>('/api/tones', {
          name,
          content,
          defaultToneId,
        }, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
      ))

      set(produce((s: ToneState) => {
        s.list = [
          data,
          ...s.list.filter(item => item.id !== defaultToneId),
        ]
      }))

      return data
    }
    catch (e) {
      console.error(e)
      throw e
    }
    finally {
      set({ loading: false })
    }
  },

  update: async (id, name, content) => {
    try {
      set({ loading: true })

      const { data } = await auth(token => (
        axios.put<ToneData>(
          `/api/tones/${id}`,
          {
            name,
            content,
          } satisfies UpdateToneParams,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        )
      ))

      await get().fetch()

      return data
    }
    catch (e) {
      console.error(e)
      throw e
    }
    finally {
      set({ loading: false })
    }
  },

  delete: async id => {
    try {
      set({ loading: true })

      await auth(token => (
        axios.delete(`/api/tones/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
      ))

      await get().fetch()
    }
    catch (e) {
      console.error(e)
    }
    finally {
      set({ loading: false })
    }
  },

  reset() {
    set({
      loading: false,
      list: [],
    })
  },
}))
